import { useQuery } from "convex/react";
import type { LucideIcon } from "lucide-react";
import {
	CheckCircle2Icon,
	ExternalLinkIcon,
	LandmarkIcon,
	ShieldAlertIcon,
	TriangleAlertIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type {
	EoirCheckResult,
	EoirCheckStatus,
} from "@/modules/threads/messageHelpers";
import { api } from "../../../convex/_generated/api";

interface EoirCheckCardProps {
	payload: EoirCheckResult;
}

type StatusStyle = {
	icon: LucideIcon;
	iconClass: string;
	badge: {
		label: string;
		variant: "default" | "secondary" | "outline" | "destructive";
	};
};

const STATUS_STYLE: Record<EoirCheckStatus, StatusStyle> = {
	solved: {
		icon: CheckCircle2Icon,
		iconClass: "bg-success/10 text-success ring-success/20",
		badge: { label: "Solved", variant: "default" },
	},
	captcha: {
		icon: ShieldAlertIcon,
		iconClass: "bg-warning/10 text-warning ring-warning/20",
		badge: { label: "Captcha — your turn", variant: "default" },
	},
	failed: {
		icon: ShieldAlertIcon,
		iconClass: "bg-warning/10 text-warning ring-warning/20",
		badge: { label: "Solve failed", variant: "secondary" },
	},
	submitted: {
		icon: CheckCircle2Icon,
		iconClass: "bg-success/10 text-success ring-success/20",
		badge: { label: "Responded", variant: "secondary" },
	},
	no_result: {
		icon: LandmarkIcon,
		iconClass: "bg-primary/10 text-primary ring-primary/20",
		badge: { label: "No result", variant: "outline" },
	},
	error: {
		icon: TriangleAlertIcon,
		iconClass: "bg-destructive/10 text-destructive ring-destructive/20",
		badge: { label: "Error", variant: "destructive" },
	},
};

function formatANumber(raw: string): string {
	const d = raw.replace(/\D/g, "").padStart(9, "0").slice(-9);
	return `A${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

export function EoirCheckCard({ payload }: EoirCheckCardProps) {
	const style = STATUS_STYLE[payload.status];
	const Icon = style.icon;
	// Recording link: staff only.
	const isStaff = useQuery(api.staff.isCurrentUserStaff) === true;
	let badgeLabel = style.badge.label;
	if (payload.status === "solved" && payload.solvedBy === "vision") {
		badgeLabel = "Solved automatically";
	} else if (payload.status === "failed" && payload.retryable) {
		badgeLabel = "Solve failed — retrying";
	}

	return (
		<Card className="my-3 w-full max-w-[42rem] border border-border" size="sm">
			<CardHeader className="gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div
						className={cn(
							"flex size-9 shrink-0 items-center justify-center rounded-lg ring-1",
							style.iconClass,
						)}
					>
						<Icon className="size-4" />
					</div>
					<div className="min-w-0 flex-1">
						<CardTitle className="truncate">
							{formatANumber(payload.alienNumber)} · {payload.nationality}
						</CardTitle>
						<CardDescription className="mt-1">
							Immigration court (EOIR / ACIS) check
						</CardDescription>
					</div>
				</div>
				<CardAction>
					<Badge variant={style.badge.variant}>{badgeLabel}</Badge>
				</CardAction>
			</CardHeader>

			<CardContent className="flex flex-col gap-3">
				<p className="text-sm text-muted-foreground">{payload.message}</p>
				{payload.caseSummary ? (
					<pre className="whitespace-pre-wrap rounded-lg bg-muted/40 px-3 py-2.5 font-sans text-sm">
						{payload.caseSummary}
					</pre>
				) : null}
				{payload.screenshotUrl ? (
					<a
						href={payload.screenshotUrl}
						target="_blank"
						rel="noreferrer"
						className="block overflow-hidden rounded-lg border border-border"
					>
						<img
							src={payload.screenshotUrl}
							alt="ACIS browser session"
							className="max-h-72 w-full object-cover object-top"
						/>
					</a>
				) : null}
			</CardContent>

			{payload.replayUrl && isStaff ? (
				<CardFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
					<a
						href={payload.replayUrl}
						target="_blank"
						rel="noreferrer"
						className={cn(
							buttonVariants({ variant: "ghost", size: "sm" }),
							"w-full sm:w-auto",
						)}
					>
						View recording
						<ExternalLinkIcon data-icon="inline-end" />
					</a>
				</CardFooter>
			) : null}
		</Card>
	);
}
